import type { ProxyFetchFailedPayload } from '../lib/connector';
import { useMaestroStore } from '../store/maestroStore';
import { showImperativeToast } from '../providers/UIProvider';
import { diagnosticLog } from './diagnosticLogger';
import { getTauriDesktopOs } from './platform';
import { isDeepBufferTimerRenderer } from '../transport/types';

const TOAST_MS = 14_000;
/** Same play attempt — proxy fetch check fires ~10s after dispatch. */
const ATTEMPT_WINDOW_MS = 120_000;
/** Suppress proxy-fetch-failed toast when renderer playback is confirmed healthy. */
const PLAYBACK_SUCCESS_SUPPRESS_MS = 30_000;
/** Transport-truth window for pairing SOAP/STOPPED failures with proxy telemetry. */
const TRANSPORT_FAILURE_WINDOW_MS = 120_000;

export type IsolationFailureBanner = {
  visible: boolean;
  rendererName: string;
  message: string;
};

type TrackedFailure = { at: number; payload: ProxyFetchFailedPayload };

const shownThisSession = new Set<string>();
const recentFailures = new Map<string, TrackedFailure>();
const attemptStartedAt = new Map<string, number>();
const playSucceededAt = new Map<string, number>();
const transportPlayFailedAt = new Map<string, number>();

let isolationBanner: IsolationFailureBanner | null = null;
const isolationBannerListeners = new Set<() => void>();

function notifyIsolationBannerListeners(): void {
  for (const fn of isolationBannerListeners) {
    try { fn(); } catch { /* ignore */ }
  }
}

export function getIsolationFailureBanner(): IsolationFailureBanner | null {
  return isolationBanner;
}

export function subscribeIsolationFailureBanner(listener: () => void): () => void {
  isolationBannerListeners.add(listener);
  return () => { isolationBannerListeners.delete(listener); };
}

export function dismissIsolationFailureBanner(): void {
  if (!isolationBanner?.visible) return;
  isolationBanner = { ...isolationBanner, visible: false };
  notifyIsolationBannerListeners();
}

function showIsolationFailureBanner(payload: ProxyFetchFailedPayload, message: string): void {
  isolationBanner = {
    visible: true,
    rendererName: payload.renderer_name || 'Your renderer',
    message,
  };
  notifyIsolationBannerListeners();
}

function normalizePlatform(raw: unknown): 'macos' | 'windows' | 'linux' | 'other' {
  const p = String(raw ?? '').trim().toLowerCase();
  if (p === 'macos' || p === 'darwin' || p === 'mac') return 'macos';
  if (p === 'windows' || p === 'win32' || p === 'win') return 'windows';
  if (p === 'linux') return 'linux';
  return getTauriDesktopOs();
}

function resolveActiveRendererContext(): {
  renderer: { id: string; name?: string } | null;
  zone: ReturnType<typeof useMaestroStore.getState>['discovery']['activeZone'];
} {
  const store = useMaestroStore.getState();
  const renderer = store.activeConnectorRenderer;
  const zone =
    store.discovery?.activeZone?.id === renderer?.id
      ? store.discovery.activeZone
      : store.discovery?.discoveredZones?.find((z) => z.id === renderer?.id) ?? store.discovery?.activeZone ?? null;
  return { renderer, zone };
}

function isDeepBufferActiveRenderer(
  rendererId?: string | null,
  rendererName?: string | null,
): boolean {
  const { renderer, zone } = resolveActiveRendererContext();
  if (!renderer) return false;
  if (rendererId && renderer.id !== rendererId) return false;
  if (rendererName && renderer.name && rendererName !== renderer.name) return false;
  return isDeepBufferTimerRenderer(renderer, zone);
}

/** User-facing guidance when a network renderer never fetched the proxy stream. */
export function buildProxyFetchFailedGuidance(
  rendererName: string,
  platform?: 'macos' | 'windows' | 'linux' | 'other',
  selfProbe?: 'ok' | 'fail',
): string {
  const name = (rendererName || 'Your renderer').trim();

  if (selfProbe === 'ok') {
    return `${name} can't reach this computer over your network, even though this computer is healthy. This is usually router 'client/AP isolation' or a guest-network setting — check your router's Wi-Fi settings, or try a wired connection.`;
  }

  const os = platform ?? getTauriDesktopOs();
  const lead = `${name} accepted the connection but can't download audio from this computer — usually a firewall.`;
  if (os === 'macos') {
    return `${lead} macOS: System Settings → Network/Firewall → allow Maestro.`;
  }
  if (os === 'windows') {
    return `${lead} Windows: allow Maestro through Windows Defender Firewall.`;
  }
  return `${lead} Check that this computer allows incoming connections from devices on your network.`;
}

function sessionKey(payload: ProxyFetchFailedPayload): string {
  return payload.renderer_ip || payload.renderer_name || 'unknown';
}

function recordFailure(payload: ProxyFetchFailedPayload): void {
  const entry: TrackedFailure = { at: Date.now(), payload };
  recentFailures.set(sessionKey(payload), entry);
  if (payload.renderer_ip) recentFailures.set(payload.renderer_ip, entry);
  if (payload.renderer_name) recentFailures.set(payload.renderer_name, entry);

  const store = useMaestroStore.getState();
  const rid = store.activeConnectorRenderer?.id || store.discovery?.activeZone?.id;
  if (rid) recentFailures.set(rid, entry);
}

/** SOAP Play failure or renderer STOPPED — not proxy-fetch timing alone. */
export function markTransportPlayFailure(rendererId?: string | null): void {
  if (!rendererId) return;
  transportPlayFailedAt.set(rendererId, Date.now());
}

export function hasRecentTransportPlayFailure(rendererId?: string | null): boolean {
  if (!rendererId) return false;
  const at = transportPlayFailedAt.get(rendererId);
  if (at == null) return false;
  return Date.now() - at < TRANSPORT_FAILURE_WINDOW_MS;
}

/** Call when a network-renderer play dispatch succeeds (starts attempt window). */
export function markNetworkPlayAttemptStarted(rendererId?: string | null): void {
  if (!rendererId) return;
  attemptStartedAt.set(rendererId, Date.now());
}

/** Call when a network renderer transitions to confirmed remote playback. */
export function markNetworkPlaySucceeded(
  rendererIp?: string | null,
  rendererName?: string | null,
): void {
  const now = Date.now();
  const ip = String(rendererIp ?? '').trim();
  const name = String(rendererName ?? '').trim();
  if (ip) playSucceededAt.set(ip, now);
  if (name) playSucceededAt.set(name, now);
}

function hasRecentPlaybackSuccess(payload: ProxyFetchFailedPayload): boolean {
  const now = Date.now();
  const keys = [
    sessionKey(payload),
    payload.renderer_ip,
    payload.renderer_name,
  ].filter(Boolean) as string[];

  for (const key of keys) {
    const at = playSucceededAt.get(key);
    if (at != null && now - at < PLAYBACK_SUCCESS_SUPPRESS_MS) {
      return true;
    }
  }
  return false;
}

function isRetryEngineRejectionMessage(errMsg: string): boolean {
  const lower = errMsg.toLowerCase();
  return (
    lower.includes('rejected the audio stream')
    || lower.includes('rejected the stream')
    || (lower.includes('setavtransporturi') && lower.includes('attempt'))
  );
}

function isSoapOrStoppedTransportFailure(errMsg: string): boolean {
  const lower = errMsg.toLowerCase();
  if (lower.includes('play failed') && (lower.includes('soap') || lower.includes('http 500'))) {
    return true;
  }
  if (lower.includes('setavtransporturi ok') && lower.includes('play')) {
    return true;
  }
  if (lower.includes('transport') && lower.includes('stopped')) {
    return true;
  }
  if (lower.includes('currenttransportstate') && lower.includes('stopped')) {
    return true;
  }
  return false;
}

function findProxyFetchFailureInAttemptWindow(
  rendererId?: string | null,
  rendererName?: string | null,
): ProxyFetchFailedPayload | null {
  const now = Date.now();
  const windowStart = rendererId && attemptStartedAt.has(rendererId)
    ? attemptStartedAt.get(rendererId)!
    : now - ATTEMPT_WINDOW_MS;

  const keys = [
    rendererId,
    rendererName,
    useMaestroStore.getState().discovery?.activeZone?.ip,
    useMaestroStore.getState().activeConnectorRenderer?.name,
  ].filter(Boolean) as string[];

  for (const key of keys) {
    const hit = recentFailures.get(key);
    if (hit && hit.at >= windowStart) return hit.payload;
  }

  let newest: TrackedFailure | null = null;
  for (const hit of recentFailures.values()) {
    if (hit.at < windowStart) continue;
    if (!newest || hit.at > newest.at) newest = hit;
  }
  return newest?.payload ?? null;
}

/** True when a recent proxy_fetch_failed had self_probe=ok (AP/client isolation). */
export function hasRecentIsolationFailure(
  rendererId?: string | null,
  rendererName?: string | null,
): boolean {
  const match = findProxyFetchFailureInAttemptWindow(rendererId, rendererName);
  return match?.self_probe === 'ok';
}

function logProxyFetchTelemetry(
  payload: ProxyFetchFailedPayload,
  reason: string,
): void {
  diagnosticLog('warn', 'playback', 'proxy_fetch_failed telemetry', {
    reason,
    renderer_name: payload.renderer_name,
    renderer_ip: payload.renderer_ip,
    stream_host: payload.stream_host,
    stream_port: payload.stream_port,
    platform: payload.platform,
    self_probe: payload.self_probe ?? null,
    device_never_reached_us: true,
  });
}

/** Connector `proxy_fetch_failed` — toast once per renderer per session (unless deep-buffer class). */
export function handleProxyFetchFailedEvent(payload: ProxyFetchFailedPayload): void {
  recordFailure(payload);

  if (hasRecentPlaybackSuccess(payload)) {
    console.log(
      `[proxyFetchFailedUI] suppressed: playback healthy for ${payload.renderer_name}`,
    );
    return;
  }

  const store = useMaestroStore.getState();
  const rendererId = store.activeConnectorRenderer?.id || store.discovery?.activeZone?.id;
  const deepBuffer = isDeepBufferActiveRenderer(rendererId, payload.renderer_name);

  if (deepBuffer) {
    logProxyFetchTelemetry(payload, 'deep_buffer_timer_renderer');
    console.log(
      `[proxyFetchFailedUI] telemetry-only for deep-buffer renderer ${payload.renderer_name} — no user toast`,
    );
    return;
  }

  const key = sessionKey(payload);

  if (payload.self_probe === 'ok') {
    const message = buildProxyFetchFailedGuidance(
      payload.renderer_name,
      normalizePlatform(payload.platform),
      payload.self_probe,
    );
    if (!shownThisSession.has(key)) {
      shownThisSession.add(key);
      showIsolationFailureBanner(payload, message);
      diagnosticLog('warn', 'playback', 'proxy_fetch_failed isolation banner shown', {
        renderer_name: payload.renderer_name,
        renderer_ip: payload.renderer_ip,
        self_probe: payload.self_probe,
        guidance: message,
      });
    }
    return;
  }

  if (!hasRecentTransportPlayFailure(rendererId)) {
    logProxyFetchTelemetry(payload, 'no_confirmed_transport_failure');
    console.log(
      `[proxyFetchFailedUI] telemetry-only — no SOAP/STOPPED transport failure for ${payload.renderer_name}`,
    );
    return;
  }

  if (shownThisSession.has(key)) return;
  shownThisSession.add(key);

  const message = buildProxyFetchFailedGuidance(
    payload.renderer_name,
    normalizePlatform(payload.platform),
    payload.self_probe,
  );
  showImperativeToast(message, TOAST_MS);
  diagnosticLog('warn', 'playback', 'proxy_fetch_failed guidance shown', {
    renderer_name: payload.renderer_name,
    renderer_ip: payload.renderer_ip,
    stream_host: payload.stream_host,
    stream_port: payload.stream_port,
    platform: payload.platform,
    self_probe: payload.self_probe ?? null,
    guidance: message,
  });
}

/**
 * When the retry engine reports a stream rejection but the renderer never fetched
 * from the proxy, replace misleading "rejected the stream" copy with firewall guidance.
 */
export function rewritePlayErrorIfProxyFetchBlocked(
  errMsg: string,
  rendererId?: string | null,
  rendererName?: string | null,
): string {
  if (!errMsg || !isRetryEngineRejectionMessage(errMsg)) return errMsg;

  const match = findProxyFetchFailureInAttemptWindow(rendererId, rendererName);
  if (!match) return errMsg;

  if (isDeepBufferActiveRenderer(rendererId, rendererName)) {
    return errMsg;
  }

  const transportConfirmed =
    hasRecentTransportPlayFailure(rendererId)
    || isSoapOrStoppedTransportFailure(errMsg);
  if (!transportConfirmed) return errMsg;

  const name = rendererName || match.renderer_name || 'Your renderer';
  const rewritten = buildProxyFetchFailedGuidance(
    name,
    normalizePlatform(match.platform),
    match.self_probe,
  );
  diagnosticLog('warn', 'playback', 'proxy_fetch_failed error rewrite', {
    original_error_truncated: errMsg.slice(0, 240),
    renderer_id: rendererId ?? null,
    renderer_name: name,
    renderer_ip: match.renderer_ip,
    self_probe: match.self_probe ?? null,
    guidance: rewritten,
  });
  return rewritten;
}

/** @deprecated Import from playFailureUI — kept for existing call sites. */
export { showNetworkPlayFailureToast } from './playFailureUI';
